import { PHASE_BOUNDARY_KEYS, STRUCTURAL_DATE_KEYS } from "../../../../../constants/project-dates/projectDateFieldKeys";
import { getDateFieldLabel } from "../../../../../constants/project-dates/projectDateFieldMetadata";
import { parseProjectPhase, ProjectPhase } from "../../../../../constants/project-phase";
import { ProjectDates, ProjectDatesData } from "../../../../../models";
import { SearchWorkflowTaskMilestonesResponse } from "../../../../../service/query";
import {
  GanttKeyDate,
  GanttPhaseBar,
  GanttPhaseKey,
  GanttRoadmapMilestone,
  GanttRoadmapRow,
} from "../../../../../widget";

type WorkflowTaskNode = SearchWorkflowTaskMilestonesResponse["results"][number];

/**
 * `ProjectPhase` is a string enum, so its members are nominally typed and are not assignable to the
 * chart's literal union even though the values match. This map is the one place the two vocabularies
 * are pinned together — a phase added to the enum fails to compile here rather than silently
 * colouring grey at runtime.
 */
const PHASE_KEY_BY_PROJECT_PHASE: Record<ProjectPhase, GanttPhaseKey> = {
  [ProjectPhase.feasibility]: "feasibility",
  [ProjectPhase.design]: "design",
  [ProjectPhase.implementation]: "implementation",
  [ProjectPhase.ongoingMonitoring]: "ongoingMonitoring",
};

const PHASE_BAR_ID_SEPARATOR = ":phase:";

const toPhaseBarId = (projectUuid: string, key: GanttPhaseKey): string =>
  `${projectUuid}${PHASE_BAR_ID_SEPARATOR}${key}`;

/**
 * Inverse of `toPhaseBarId` — the phase a clicked bar belongs to, or `undefined` for any other marker
 * id. `parseProjectPhase` resolves the key, since a `GanttPhaseKey` is a `ProjectPhase` value by
 * construction (see `PHASE_KEY_BY_PROJECT_PHASE`, which pins the two vocabularies together).
 */
export const toProjectPhaseFromPhaseBarId = (phaseBarId: string): ProjectPhase | undefined =>
  parseProjectPhase(phaseBarId.split(PHASE_BAR_ID_SEPARATOR)[1]);

/**
 * Workflow steps report their phase in title case with spaces ("Ongoing monitoring"), so it is
 * normalised through `parseProjectPhase` rather than compared literally. An unknown or missing phase
 * yields `undefined`: the chart then colours the marker from the phase bar its date sits inside, and
 * only falls back to grey when it sits outside every bar.
 */
const toPhaseKey = (value: string | null | undefined): GanttPhaseKey | undefined => {
  const phase = parseProjectPhase(value);

  return phase ? PHASE_KEY_BY_PROJECT_PHASE[phase] : undefined;
};

interface PhaseBarSource {
  key: GanttPhaseKey;
  /** Legacy roadmap wording — the tooltips read from this. */
  label: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
}

/**
 * The four fixed project phases, as the legacy roadmap drew them.
 *
 * Ongoing monitoring is emitted as the single overall span, never as one bar per cycle: the chart
 * runs `normalizePhaseRanges` over the bars, clamping each bar's end to the next bar's start, so
 * cycle bars nested inside the overall bar would truncate the overall bar at the first cycle. Cycle
 * assessments stay point-in-time key dates instead (see `toKeyDates`).
 *
 * A bar is drawn only when both ends are known — matching the legacy `createRow` guard — and a
 * backwards range is dropped, since the chart floors bar width at 4px and would anchor the stub at
 * the wrong date.
 */
const toPhaseBars = (projectUuid: string, dates: ProjectDates): GanttPhaseBar[] => {
  const sources: PhaseBarSource[] = [
    { key: "feasibility", label: "Feasibility", startDate: dates.feasibility?.start, endDate: dates.feasibility?.end },
    { key: "design", label: "Design", startDate: dates.design?.start, endDate: dates.design?.end },
    {
      key: "implementation",
      label: "Implementation",
      startDate: dates.implementation?.start,
      endDate: dates.implementation?.end,
    },
    {
      key: "ongoingMonitoring",
      label: "Ongoing monitoring",
      startDate: dates.ongoingMonitoring?.overallStart,
      endDate: dates.ongoingMonitoring?.overallEnd,
    },
  ];

  return sources.reduce<GanttPhaseBar[]>((bars, { key, label, startDate, endDate }) => {
    if (!startDate || !endDate || endDate.getTime() < startDate.getTime()) return bars;

    return [...bars, { id: toPhaseBarId(projectUuid, key), label, phase: key, startDate, endDate }];
  }, []);
};

const ASSESSMENT_ORDINALS = [
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
  "Eleventh",
  "Twelfth",
  "Thirteenth",
  "Fourteenth",
  "Fifteenth",
];

const toAssessmentLabel = (sequence: number): string => {
  const ordinal = ASSESSMENT_ORDINALS[sequence - 1];

  return ordinal ? `${ordinal} assessment date` : `Assessment date (cycle ${sequence})`;
};

const KEY_DATE_GROUPS: GanttPhaseKey[] = ["feasibility", "design", "implementation", "ongoingMonitoring"];

const toKeyDates = (projectUuid: string, dates: ProjectDates): GanttKeyDate[] => {
  const datesRecord = dates as unknown as Record<string, unknown>;

  const phaseKeyDates = KEY_DATE_GROUPS.flatMap<GanttKeyDate>((group) => {
    const values = (datesRecord[group] ?? {}) as Record<string, unknown>;

    return Object.entries(values).reduce<GanttKeyDate[]>((keyDates, [key, value]) => {
      if (!(value instanceof Date) || STRUCTURAL_DATE_KEYS.has(key) || PHASE_BOUNDARY_KEYS.has(key)) return keyDates;

      return [
        ...keyDates,
        {
          // Flattened path as the id suffix, so it is stable across refetches and the edit-dates
          // round-trip — never an array index or the date itself.
          id: `${projectUuid}:keyDate:${group}.${key}`,
          label: getDateFieldLabel(group, key),
          phase: group,
          date: value,
        },
      ];
    }, []);
  });

  const cycleKeyDates = (dates.ongoingMonitoring?.cycles ?? []).reduce<GanttKeyDate[]>((keyDates, cycle) => {
    if (!cycle.assessmentDate) return keyDates;

    return [
      ...keyDates,
      {
        id: `${projectUuid}:keyDate:ongoingMonitoring.cycles.${cycle.uuid}.assessmentDate`,
        label: toAssessmentLabel(cycle.sequence),
        phase: "ongoingMonitoring",
        date: cycle.assessmentDate,
      },
    ];
  }, []);

  return [...phaseKeyDates, ...cycleKeyDates].sort((a, b) => a.date.getTime() - b.date.getTime());
};

/**
 * Every task in the list becomes a diamond at its deadline. The page's query already filters
 * `isMilestone eq true`, so the whole list is milestones — including tasks bound to a project date
 * field. Those are diamonds and not key-date bars: the field itself is already drawn from `dates`,
 * and a bar would lose the milestone click-through. Legacy drew both markers too, and the projection
 * carries no `keyDate` for this mapper to reroute on even if it wanted to.
 *
 * The task uuid is the milestone id so `onMilestoneClick(milestoneId, rowId)` can resolve back to the
 * task. A task with no deadline is dropped: the chart positions by date, and although the query
 * excludes them, `EditDatesPanel` writes milestones back into the same state.
 *
 * Same-day milestones are deliberately not merged into one comma-joined label as legacy did — the
 * chart clusters markers by pixel proximity and lists each label in the cluster's popover, so merging
 * here would repeat "A, B" inside a single cluster.
 */
const toMilestones = (milestones: WorkflowTaskNode[] | undefined): GanttRoadmapMilestone[] =>
  (milestones ?? []).reduce<GanttRoadmapMilestone[]>((acc, task) => {
    if (!task.deadlineDate) return acc;

    return [...acc, { id: task.uuid, label: task.name, date: task.deadlineDate, phase: toPhaseKey(task.step?.phase) }];
  }, []);

/**
 * Project roadmap page data → `GanttChart` rows (`variant="project-roadmap"`, `mode="phase"`).
 *
 * The server's ordering is kept and no project is dropped: a project with no complete phase and no
 * milestones still yields a row, so the roadmap keeps matching the user's project selection rather
 * than silently shrinking.
 *
 * Dates are passed through by reference and never re-anchored to start-of-day — they are already
 * `Date`s revived from the project's dates JSON, and shifting them here would put the markers
 * somewhere other than where the edit-dates panel says they are.
 */
export const toGanttRoadmapRows = (projects: ProjectDatesData[]): GanttRoadmapRow[] =>
  projects.map((project) => ({
    id: project.uuid,
    name: project.displayName,
    // Legacy printed the project's status under its name.
    subtitle: project.currentStatus || undefined,
    phases: toPhaseBars(project.uuid, project.dates),
    milestones: toMilestones(project.milestones),
    keyDates: toKeyDates(project.uuid, project.dates),
  }));
